import type { PatientBillingCharge } from '@/api/types/PatientApi'
import { colors } from '@/constants/colors'
import { formatCents } from '@/lib/money'
import { Button, ButtonText } from '@/ui/core/button'
import { SheetTitle } from '@/ui/core/sheet'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { format } from 'date-fns'
import { View } from 'react-native'

const STATUS: Partial<Record<PatientBillingCharge['status'], string>> = {
	SUCCEEDED: 'Paid',
	PENDING: 'Pending',
	FAILED: "Didn't go through",
	REFUNDED: 'Refunded',
	PARTIALLY_REFUNDED: 'Partly refunded',
	DISPUTED: 'Disputed',
	EXPIRED: 'Expired',
	CANCELED: 'Cancelled',
}

const Fact = ({
	label,
	value,
	last = false,
}: { label: string; value: string; last?: boolean }) => (
	<View
		className="flex-row items-center justify-between py-3.5"
		style={last ? undefined : { borderBottomWidth: 1, borderBottomColor: colors.divider }}
	>
		<Text size="body1" tone="soft">
			{label}
		</Text>
		<Text size="body0" weight="bold">
			{value}
		</Text>
	</View>
)

/**
 * One payment, laid out: what it was for, how much, when, where it stands, and the receipt to
 * keep when money changed hands. Lives inside the payments sheet.
 */
export const PaymentDetail = ({
	charge,
	onBack,
	onDownload,
	isDownloading,
}: {
	charge: PatientBillingCharge
	onBack?: () => void
	onDownload?: (charge: PatientBillingCharge) => void
	isDownloading: boolean
}) => (
	<View className="pt-2 pb-2">
		<SheetTitle glyph="dollar" onBack={onBack}>
			{charge.description || 'Payment'}
		</SheetTitle>
		<VStack gap={20}>
			{/* Facts and button share the title's 16pt inset, so one column runs down the sheet:
			    the label, the value, the dividers and the button all start and end together. */}
			<View className="px-4">
				<Fact label="Amount" value={formatCents(charge.amountInCents)} />
				{!!charge.refundedInCents && (
					<Fact label="Refunded" value={formatCents(charge.refundedInCents)} />
				)}
				<Fact label="Date" value={format(charge.date, 'MMM d, yyyy')} />
				<Fact label="Status" value={STATUS[charge.status] ?? charge.status} last />
			</View>
			{onDownload && (
				<View className="px-4">
					<Button
						variant="primary"
						size="lg"
						fullWidth
						disabled={isDownloading}
						onPress={() => onDownload(charge)}
					>
						<ButtonText>Download Receipt</ButtonText>
					</Button>
				</View>
			)}
		</VStack>
	</View>
)
